"use client";

import { useEffect, useRef } from "react";
import dynamic from "next/dynamic";
import { playSound } from "@/lib/game/audio";
import { isRocketFlyable, rocketPlacementsForDisplay } from "@/lib/game/placements";
import type { BuilderSceneHandle } from "@/components/PixiBuilder";
import { GameIconButton } from "../game-ui";
import { ErrorBoundary } from "../ErrorBoundary";
import { useGame } from "../GameProvider";

const PixiBuilder = dynamic(
  () => import("../PixiBuilder").then((m) => m.PixiBuilder),
  {
    ssr: false,
    loading: () => (
      <div className="flex h-full min-h-[280px] items-center justify-center bg-sky-400 text-white">
        <p className="text-lg font-semibold">Loading builder…</p>
      </div>
    ),
  },
);

export function BuilderScreen() {
  const { goTo, progress, trackBuilderVisit, trackRocketLaunch } = useGame();
  const builderRef = useRef<BuilderSceneHandle>(null);
  const canFly = isRocketFlyable(rocketPlacementsForDisplay(progress.builtRocket));

  useEffect(() => {
    trackBuilderVisit();
  }, [trackBuilderVisit]);

  return (
    <div className="relative h-full min-h-0 flex-1">
      <div className="pointer-events-none absolute inset-x-0 top-0 z-10 flex items-start justify-between gap-2 p-2">
        <GameIconButton
          icon="back"
          tone="slate"
          size="sm"
          className="pointer-events-auto"
          onClick={() => goTo("home")}
        />
        <div className="pointer-events-auto flex shrink-0 gap-1.5">
          <GameIconButton
            icon="clear"
            tone="slate"
            size="sm"
            onClick={() => builderRef.current?.clearPlacements()}
          />
          <GameIconButton
            icon="random"
            tone="violet"
            size="sm"
            onClick={() => builderRef.current?.randomizePlacements()}
          />
          <GameIconButton
            icon="fly"
            tone="amber"
            size="sm"
            disabled={!canFly}
            onClick={() => {
              if (!canFly) return;
              trackRocketLaunch();
              playSound("launch");
              goTo("fly");
            }}
          />
        </div>
      </div>

      <ErrorBoundary label="Rocket Builder">
        <PixiBuilder ref={builderRef} />
      </ErrorBoundary>

      {!canFly && (
        <p className="pointer-events-none absolute bottom-3 left-0 right-0 z-10 text-center text-xs font-semibold text-white drop-shadow">
          Need nose, fuselage & engine to launch
        </p>
      )}
    </div>
  );
}
